import { useCallback, useEffect, useRef } from 'react';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { DealerManager } from '@/utils/dealerManager';
import { logger } from '@/utils/logger';
import {
  shouldUseShiftDigitalTracking,
  trackShiftChatImpression,
  trackShiftChatClick,
  trackShiftChatMessage,
  trackShiftChatLead,
  trackShiftChatClosed,
  determineProfitCenter
} from '@/utils/shiftDigitalTracking';

export const useShiftDigitalTracking = (sessionId: string | null) => {
  const { sessionData } = useDealerSession();
  const impressionTrackedRef = useRef(false);
  const lastSessionIdRef = useRef<string | null>(null);
  const isInitialMount = useRef(true);
  const lastTrackedMessageRef = useRef<{ key: string; at: number } | null>(null);

  // Reset tracking on initial mount or when session changes
  useEffect(() => {
    // Reset on first mount
    if (isInitialMount.current) {
      impressionTrackedRef.current = false;
      lastSessionIdRef.current = sessionId;
      isInitialMount.current = false;
      return;
    }
    
    // Reset when session changes
    if (sessionId && sessionId !== lastSessionIdRef.current) {
      impressionTrackedRef.current = false;
      lastSessionIdRef.current = sessionId;
    }
  }, [sessionId]);

  // Check if this dealer should use Shift Digital tracking
  const shouldTrack = useCallback(() => {
    const dealer = DealerManager.getCurrentDealer();
    return shouldUseShiftDigitalTracking(dealer?.certification_name);
  }, []);

  /**
   * Track chat impression - called when chat component mounts
   */
  const trackImpression = useCallback(() => {
    if (!sessionId || impressionTrackedRef.current || !shouldTrack()) {
      return;
    }
    
    const profitCenter = determineProfitCenter();
    const success = trackShiftChatImpression(sessionId, profitCenter);
    if (success) {
      impressionTrackedRef.current = true;
    }
  }, [sessionId, shouldTrack]);

  /**
   * Track chat click - called when chat is expanded (fires every time)
   */
  const trackClick = useCallback(() => {
    if (!sessionId || !shouldTrack()) {
      return;
    }
    
    const profitCenter = determineProfitCenter();
    trackShiftChatClick(sessionId, profitCenter);
  }, [sessionId, shouldTrack]);

  /**
   * Track message (user or agent)
   */
  const trackMessage = useCallback((messageContent: string, messageType: 'user' | 'agent', formType?: string) => {
    if (!sessionId || !shouldTrack()) {
      return;
    }

    const normalized = messageContent?.trim();
    if (!normalized) return;

    const profitCenter = determineProfitCenter(formType);
    const dedupeKey = `${messageType}:${profitCenter}:${normalized}`;
    const now = Date.now();

    if (lastTrackedMessageRef.current?.key === dedupeKey && (now - lastTrackedMessageRef.current.at) < 120000) {
      return;
    }

    lastTrackedMessageRef.current = { key: dedupeKey, at: now };
    trackShiftChatMessage(sessionId, normalized, messageType, profitCenter);
  }, [sessionId, shouldTrack]);

  /**
   * Track lead submission
   */
  const trackLead = useCallback((leadId: string, formType?: string) => {
    if (!sessionId || !shouldTrack()) return;
    
    const profitCenter = determineProfitCenter(formType);
    trackShiftChatLead(sessionId, leadId, profitCenter);
  }, [sessionId, shouldTrack]);

  /**
   * Track chat closed
   */
  const trackClosed = useCallback((formType?: string) => {
    if (!sessionId || !shouldTrack()) return;
    
    const profitCenter = determineProfitCenter(formType);
    trackShiftChatClosed(sessionId, profitCenter);
  }, [sessionId, shouldTrack]);


  const isShiftDigitalEnabled = shouldTrack();
  
  return {
    trackImpression,
    trackClick,
    trackMessage,
    trackLead,
    trackClosed,
    isShiftDigitalEnabled
  };
};
